import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldOff, TriangleAlert } from "lucide-react";
import {
  MissingVerifierError,
  TokenExchangeError,
  beginLogin,
  getCurrentPrincipal,
  handleSsoCallback,
  logout,
  redirectToLogin,
} from "@/api/client";
import { clearVerifierRetry, consumeVerifierRetry } from "./ssoRetryGuard";
import { AuthNotice, AuthPending } from "./AuthScreens";

/**
 * Точка возврата из signaris-auth (OAuth2 Authorization Code + PKCE).
 * URL: `/auth/callback?code=<one-time>&state=...`
 *
 * Обмен code → токены делает общий оркестратор (`handleSsoCallback`), здесь —
 * навигация по роли и терминальные экраны. Ни на одной ошибке отсюда НЕ уходим
 * в новый вход без предохранителя: auth молча выдаёт новый код, и на постоянной
 * причине (нет роли NET, нет verifier) получается бесконечная петля.
 */
type Screen =
  | { kind: "pending" }
  | { kind: "no-role"; email: string | null }
  | { kind: "failed"; detail: string | null };

export function AuthCallback() {
  const nav = useNavigate();
  // Адрес читаем ОДИН раз: либа стирает `?code=` из адреса, в том числе
  // синхронно перед throw MissingVerifierError, а StrictMode зовёт effect дважды.
  const search = useRef(window.location.search).current;
  const [screen, setScreen] = useState<Screen>({ kind: "pending" });

  useEffect(() => {
    let live = true;

    if (search === "" || search === "?") {
      // Заход руками или reload уже обработанного callback: кода нет и не
      // будет. Решение отдаём гарду /admin — там либо сессия, либо вход.
      nav("/admin", { replace: true });
      return;
    }

    handleSsoCallback(search)
      .then(({ returnPath }) => {
        clearVerifierRetry();
        if (!live) return;
        const principal = getCurrentPrincipal();
        // Роль проверяем ДО навигации по returnPath: сотруднику без роли NET
        // глубокая ссылка не поможет (гард /admin увёл бы его в /my, где
        // пустой кабинет и 403 от /v1/me — вид обманчивый). Новый вход здесь
        // тоже нельзя: auth молча выдаст новый код, роль от этого не появится.
        if (!principal?.net_role) {
          setScreen({ kind: "no-role", email: principal?.email ?? null });
          return;
        }
        if (returnPath && returnPath !== "/admin") {
          nav(returnPath, { replace: true });
          return;
        }
        nav(principal.net_role === "user" ? "/my" : "/admin", {
          replace: true,
        });
      })
      .catch((err: unknown) => {
        if (err instanceof MissingVerifierError) {
          const decision = consumeVerifierRetry();
          if (decision === "pending") return; // повтор уже идёт (StrictMode)
          if (decision === "retry") {
            console.warn(
              "[net-auth] callback без PKCE-verifier — один автоповтор входа",
              err,
            );
            redirectToLogin("/admin").catch((e) => {
              console.error("[net-auth] автоповтор входа не начался", e);
              if (live) setScreen({ kind: "failed", detail: null });
            });
            return; // остаёмся на «Завершаем вход…»
          }
          console.error(
            "[net-auth] callback без PKCE-verifier повторно — прекращаем",
            err,
          );
        } else if (err instanceof TokenExchangeError) {
          // 4xx — код сожжён, повтор бесполезен; 5xx — мог уцелеть, но решение
          // оставляем человеку, чтобы не молотить auth автоматически.
          console.error(`[net-auth] обмен кода не удался: ${err.status}`, err);
          if (live) {
            setScreen({ kind: "failed", detail: `Код ошибки: ${err.status}` });
          }
          return;
        } else {
          console.error("[net-auth] callback не обработан", err);
        }
        if (live) setScreen({ kind: "failed", detail: null });
      });

    return () => {
      live = false;
    };
  }, [nav, search]);

  if (screen.kind === "no-role") {
    return (
      <AuthNotice
        icon={<ShieldOff size={28} className="text-state-warning" />}
        title="Нет доступа к Signaris Net"
        description={
          <>
            {screen.email ? <><b>{screen.email}</b> — </> : null}
            у этой учётной записи нет роли в Signaris Net. Попросите
            администратора организации выдать доступ или войдите другим
            аккаунтом.
          </>
        }
        primary={{
          label: "Войти другим аккаунтом",
          onClick: () => void logout(true),
        }}
      />
    );
  }

  if (screen.kind === "failed") {
    return (
      <AuthNotice
        icon={<TriangleAlert size={28} className="text-state-danger" />}
        title="Не удалось завершить вход"
        description="Попробуйте войти снова. Если ошибка повторится — сообщите администратору время и код ошибки."
        detail={screen.detail}
        primary={{
          label: "Войти заново",
          onClick: () => beginLogin("/admin", { force: true }),
        }}
      />
    );
  }

  return <AuthPending text="Завершаем вход…" />;
}
